import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { Router } from '@angular/router';
import { AppStateService } from '@services/app-state.service';
import { ToastService } from '@services/toast.service';
import type { Group } from '@models/group.interface';
import { CompleteDailyCheckinRequest, GroupCheckinData } from '@models/complete-daily-checkin.interface';
import { GroupSelectDialogComponent } from '@components/shared';
import type { GroupSelectDialogResult } from '@components/shared';
import { Sunbed } from '@models/sunbed';
import { HotelReservation } from '@app/models';
import { ReservationService } from '@app/services';
import { HttpErrorResponse } from '@angular/common/http';
import { LoadingComponent } from '@components/shared/loading.component';
import { GuestPackageType } from '@app/utils';

@Component({
  selector: 'app-credit-assign',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    MatExpansionModule,
    MatDialogModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    LoadingComponent
  ],
  templateUrl: './credit-assign.component.html',
  styleUrls: ['./credit-assign.component.scss']
})
export class CreditAssignComponent implements OnInit {

  groups: Group[] = [];
  unassignedChairs: Sunbed[] = [];

  loading = false;

  newGroupName = '';
  newGroupContact = '';
  newGroupEmail = '';
  newGroupOverdraft = 0;

  wristbandInputs: { [groupId: string]: boolean } = {};
  newWristbandInput: { [groupId: string]: string } = {};
  wristbandLoading: { [groupId: string]: boolean } = {};

  // Sunbed number editing
  editingSunbedId: string | null = null;
  editedSunbedNumber: string = '';
  package: GuestPackageType = GuestPackageType.EXTERNAL;

  // True once complete-daily-checkin already succeeded for these groups (e.g. user navigated
  // back here from /send) - blocks re-submitting to avoid duplicating gastro master cards
  alreadyCheckedIn = false;

  constructor(
    private appState: AppStateService,
    private router: Router,
    private location: Location,
    private dialog: MatDialog,
    private reservationService: ReservationService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {

    this.appState.initializeSunbeds();

    this.groups = [...this.appState.groups];
    this.alreadyCheckedIn = this.appState.dailyCheckinCompleted;
    const reservation = this.appState.reservation;
    this.package = this.appState.guestPackage;
    if (reservation) {
      this.newGroupContact = reservation.name ?? '';
      this.newGroupEmail = reservation.email ?? '';

      const booking = this.appState.valamarBooking;
      console.log('Credit Assign booking',booking);
      if(booking){
        if(reservation.hotelReservation == null){
          console.log("No hotel res")
          reservation.hotelReservation = [];
        }
        const hotelRes:HotelReservation = {
          masterId : booking.masterId,
          stayId : booking.staysData?.[0]?.masterId
        }

        reservation.hotelReservation.push(hotelRes);

        this.reservationService.updateReservation(reservation).subscribe({
          next: updatedReservation => {
            console.log('Reservation hotel res data updated', updatedReservation);
          },
          error: err => {
            console.error('Update failed', err);
          }
        });
      }
    }

    ///group
    this.newGroupName = `Group ${this.groups.length + 1}`;
    this.updateUnassignedChairs();
  }

  updateUnassignedChairs(): void {
    this.unassignedChairs = [...this.appState.getUnassignedSunbeds()];
  }


  get dailyBudget(): number {
    return this.appState.dailyBudget;
  }

  get totalPriceUsed(): number {
    return this.groups.reduce((total, group) => total + (group.price || 0), 0);
  }

  get currency(): string {
    return this.appState.reservation?.currency ?? 'EUR';
  }

  get hasUnassignedChairs(): boolean {
    return this.unassignedChairs.length > 0;
  }

  createGroup(): void {
    const newGroup: Group = {
      id: this.appState.generateCardId(),
      name: this.newGroupName.trim(),
      contactPerson: this.newGroupContact,
      contactEmail: this.newGroupEmail,
      sunbeds: [],
      childWristbands: [],
      price: 0,
      overdraft: this.newGroupOverdraft,
      isExpanded: true
    };

    this.appState.addGroup(newGroup);
    this.groups = [...this.appState.groups];
    this.newGroupName = `Group ${this.groups.length + 1}`;
    this.newGroupOverdraft = 0;

    this.toastService.success(`Grupa "${newGroup.name}" je kreirana`);
  }

  assignChairToSelectedGroup(sunbed: Sunbed): void {
    console.log("Sunbed : ", sunbed);
    // If there are no groups, return
    if (this.groups.length === 0) {
      return;
    }

    // Show dialog for group selection and chair number input
    const dialogRef = this.dialog.open(GroupSelectDialogComponent, {
      data: {
        groups: this.groups,
        chair: sunbed
      },
      width: '600px'
    });

    dialogRef.afterClosed().subscribe((result: GroupSelectDialogResult | null) => {
      if (result) {
        this.assignChairToGroup(sunbed, result.groupId, result.chairNumber);
      }
    });
  }

  private assignChairToGroup(chair: Sunbed, groupId: string, chairNumber?: string): void {
    // Set chair number if provided
    if (chairNumber) {
      chair.number = chairNumber;
    }

    this.appState.assignSunbedToGroup(chair.id, groupId);
    this.groups = [...this.appState.groups];
    this.updateUnassignedChairs();
  }

  removeChairFromGroup(groupId: string, chairId: string): void {
    this.appState.removeSunbedFromGroup(chairId, groupId);
    this.groups = [...this.appState.groups];
    this.updateUnassignedChairs();
  }

  removeGroup(groupId: string): void {

    //todo:: implement remove master from gastro
    const group = this.groups.find(g => g.id === groupId);

    if (!group) return;

    group.sunbeds.forEach(chair => {
      this.appState.removeSunbedFromGroup(chair.id, groupId);
    });

    this.appState.removeGroup(groupId);
    this.groups = [...this.appState.groups];
    this.updateUnassignedChairs();
  }

  onGroupUpdate(groupId: string, field: keyof Group, value: any): void {

    this.appState.updateGroup(groupId, {
      [field]: value
    });

    this.groups = [...this.appState.groups];
  }

  addChildWristband(groupId: string, wristbandCode?: string): void {
    const wristbandCodeInput = wristbandCode?.trim() || this.newWristbandInput[groupId]?.trim();

    if (!wristbandCodeInput) return;

    const group = this.groups.find(g => g.id === groupId);

    if (!group) return;

    if (group.childWristbands.includes(wristbandCodeInput)) {
      this.toastService.warning('Naruknica već postoji u grupi');
      this.newWristbandInput[groupId] = '';
      this.wristbandInputs[groupId] = false;
      return;
    }

    this.appState.updateGroup(groupId, {
      childWristbands: [
        ...group.childWristbands,
        wristbandCodeInput
      ]
    });

    this.groups = [...this.appState.groups];

    this.toastService.success(`Naruknica "${wristbandCodeInput}" je spremna za check-in`);
    this.newWristbandInput[groupId] = '';
    this.wristbandInputs[groupId] = false;
  }

  toggleWristbandInput(groupId: string): void {
    this.wristbandInputs[groupId] = true;
    // Focus na input field nakon što se renderira
    setTimeout(() => {
      const inputElement = document.querySelector(`#wristband-input-${groupId}`) as HTMLInputElement;
      if (inputElement) {
        inputElement.focus();
      }
    }, 100);
  }

  cancelWristbandInput(groupId: string): void {
    console.log(
      '%cRemove additional',
      'color: orange; font-weight: bold;',
      this.newWristbandInput[groupId]
    );
    this.wristbandInputs[groupId] = false;
    this.newWristbandInput[groupId] = '';
  }

  removeChildWristband(groupId: string, wristband: string): void {

    const group = this.groups.find(g => g.id === groupId);

    if (!group) return;

    this.appState.updateGroup(groupId, {
      childWristbands: group.childWristbands.filter(w => w !== wristband)
    });

    this.groups = [...this.appState.groups];
  }

  get canContinue(): boolean {
    return (
      !this.loading &&
      !this.alreadyCheckedIn &&
      this.groups.length > 0 &&
      !this.hasUnassignedChairs &&
      this.groups.every(g => g.childWristbands.length > 0)
    );
  }

  onContinue(): void {
    if (this.loading || this.alreadyCheckedIn) {
      return;
    }

    const reservation = this.appState.reservation;

    if (!reservation) {
      console.error('No reservation found');
      this.toastService.error('Rezervacija nije pronađena');
      return;
    }

    if (!reservation.resvNameId) {
      console.info('Walkin reservation');
      reservation.resvNameId = "";
    }

    // Prepare complete daily checkin request
    const now = new Date();
    const transferDate = now.toISOString(); // Format: 2026-06-01T12:00:00.000Z

    const request: CompleteDailyCheckinRequest = {
      identifier: reservation.identifier,
      transferDate: transferDate,
      groups: this.groups.map(group => ({
        id: group.id,
        name: group.name,
        contactPerson: group.contactPerson,
        contactEmail: group.contactEmail,
        initialBalance: this.appState.isWalkIn ? 0 : this.getGroupPrice(group),
        allowedOverdraft: group.overdraft || 0,
        chairs: group.sunbeds,
        additionalCards: group.childWristbands
      }))
    };

    // Show loading overlay
    this.loading = true;

    // Send everything to backend - Gastro (master+additional) → DailyTransfer → PuroReservation

    this.reservationService.completeDailyCheckin(request).subscribe({
      next: (response) => {
        console.log('Complete daily checkin response:', response);
        this.loading = false;

        if (response.success && response.reservation) {

          // Check for failed additional cards if response has groups
          if (response.reservation.gastro.masterCards?.length) {
            // Collect all additional cards from request (flat array)
            const allRequestCards = request.groups.flatMap(g => g.additionalCards || []);
            console.log("requested cards:", allRequestCards);
            // Collect all additional cards from response (flat array of IDs)
            const allResponseCards = response.reservation.gastro.masterCards.flatMap(
              (mc: any) => (mc.additionalCards || []).map((card: any) => card.id)
            );
            console.log("allResponseCards:", allResponseCards);
            // Find cards that were sent but not returned (failed to add)
            const failedCards = allRequestCards.filter(card => !allResponseCards.includes(card));
            console.log("failedCards:", failedCards);
            if (failedCards.length > 0) {
              this.toastService.warning(
                `Sljedeće narukvice nisu uspješno dodane: ${failedCards.join(', ')}`
              );
            } else {
              this.toastService.success('Check-in uspješno završen!');
            }
            this.appState.setReservation(response.reservation);
            this.appState.setDailyCheckinCompleted(true);
            this.alreadyCheckedIn = true;
            this.appState.setCurrentStep(4);
            this.appState.setSelectedDate(transferDate)
            this.router.navigate(['/send']);
          }else{
            this.toastService.error(response.message || 'Greška prilikom check-ina , nisu se kreirale grupe');
          }
        } else {
          this.toastService.error(response.message || 'Greška prilikom check-ina');
        }
      },
      error: (err: HttpErrorResponse) => {
        console.error('Complete daily checkin failed:', err);
        this.loading = false;
        this.toastService.error('Greška prilikom slanja check-ina');
      }
    });
  }

  onBack(): void {
    this.appState.setCurrentStep(2);
    this.location.back();
  }

   formatDate = (date: Date): string => {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');

    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
  };

  getGroupPriceWithDiscount(group: Group): number {
    return group.sunbeds.reduce(
      (sum, chair) => sum + (chair.priceWithDiscount || 0),
      0
    );
  }

  getGroupPrice(group: Group): number {
    return group.sunbeds.reduce(
      (sum, chair) => sum + (chair.price || 0),
      0
    );
  }

  /**
   * Check if the current reservation is for today
   */
  get isTodayReservation(): boolean {
    const selectedDate = this.appState.selectedDate;
    if (!selectedDate) return true; // Default to true if no date selected

    const today = new Date().toISOString().split('T')[0];
    return selectedDate === today;
  }

  get isWalkIn(): boolean {
    return this.appState.isWalkIn;
  }

  /**
   * Save the edited sunbed number
   */
  saveSunbedNumber(groupId: string, sunbedId: string): void {
    const group = this.groups.find(g => g.id === groupId);
    if (!group) return;

    const sunbed = group.sunbeds.find(s => s.id === sunbedId);
    if (!sunbed) return;

    // Update the sunbed number
    sunbed.number = this.editedSunbedNumber.trim();

    // Exit edit mode
    this.editingSunbedId = null;
    this.editedSunbedNumber = '';

    // Update groups to trigger change detection
    this.groups = [...this.groups];
  }

  /**
   * Cancel editing sunbed number
   */
  cancelSunbedEdit(): void {
    this.editingSunbedId = null;
    this.editedSunbedNumber = '';
  }

  /**
   * Handle Enter key press in edit mode
   */
  onSunbedEditKeyDown(event: KeyboardEvent, groupId: string, sunbedId: string): void {
    if (event.key === 'Enter') {
      this.saveSunbedNumber(groupId, sunbedId);
    } else if (event.key === 'Escape') {
      this.cancelSunbedEdit();
    }
  }
}
